import React, { Component, ReactNode } from 'react';
import { Alert, AlertDescription } from './ui/alert';
import { AlertCircle, RefreshCw, Home, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import i18n from '@/src/i18n/config';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /** If true, shows a compact inline error instead of full-page */
  inline?: boolean;
  /** Custom reset handler - if provided, overrides default behavior */
  onReset?: () => void;
  /** Name of the section for better error messages */
  sectionName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({ errorInfo });

    // Report to the backend observability pipeline (observability_issues -> RCA).
    // The global window.onerror handler in activityTracker already catches these,
    // but only ever sees the minified JS stack — which is why a live React #310
    // sat in the issue table for six weeks as an unidentifiable `at $2s`. The
    // component stack names the component that actually blew up, so append it.
    import('@/src/lib/activityTracker')
      .then((m) =>
        m.trackError({
          error_type: error.name || 'Error',
          error_message: error.message,
          path: typeof window !== 'undefined' ? window.location?.pathname : undefined,
          stack: [error.stack, 'Component stack:', errorInfo.componentStack]
            .filter(Boolean)
            .join('\n'),
        }),
      )
      .catch(() => {
        // Reporting an error must never throw.
      });

    // Report to Datadog if available
    if (typeof window !== 'undefined') {
      import('@datadog/browser-rum').then(({ datadogRum }) => {
        try {
          datadogRum.addError(error, {
            component_stack: errorInfo.componentStack,
            section: this.props.sectionName || 'unknown',
          });
        } catch {
          // Datadog not initialized
        }
      }).catch(() => {
        // Module not available
      });
    }
  }

  handleRetry = () => {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  handleGoToLogin = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/member/login';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const sectionLabel = this.props.sectionName
        ? `in ${this.props.sectionName}`
        : '';

      // Inline compact error for section-level boundaries
      if (this.props.inline) {
        return (
          <div className="p-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-red-800 dark:text-red-200">
                  {i18n.t('errors.sectionError', `Something went wrong ${sectionLabel}`.trim())}
                </p>
                <p className="text-sm text-red-600 dark:text-red-300 mt-1 truncate">
                  {this.state.error?.message || i18n.t('errors.unexpectedError', 'An unexpected error occurred')}
                </p>
                <div className="flex gap-2 mt-3">
                  <Button onClick={this.handleRetry} variant="outline" size="sm" className="gap-1">
                    <RefreshCw className="h-3 w-3" />
                    {i18n.t('common.tryAgain', 'Try Again')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      // Full-page error for global/route-level boundaries
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
          <div className="max-w-md w-full">
            <Alert variant="destructive" className="border-red-300 dark:border-red-800">
              <AlertCircle className="h-5 w-5" />
              <AlertDescription className="mt-2">
                <p className="font-semibold text-lg mb-2">
                  {i18n.t('errors.somethingWentWrong', 'Something went wrong')}
                </p>
                <p className="text-sm mb-4 opacity-90">
                  {this.state.error?.message || i18n.t('errors.unexpectedError', 'An unexpected error occurred')}
                </p>
                <p className="text-xs mb-4 opacity-70">
                  {i18n.t('errors.tryRefresh', "Don't worry - you can try again without losing your work.")}
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button onClick={this.handleRetry} variant="default" size="sm" className="gap-1">
                    <RefreshCw className="h-3 w-3" />
                    {i18n.t('common.tryAgain', 'Try Again')}
                  </Button>
                  <Button onClick={this.handleGoHome} variant="outline" size="sm" className="gap-1">
                    <Home className="h-3 w-3" />
                    {i18n.t('common.goHome', 'Go Home')}
                  </Button>
                  <Button onClick={this.handleGoToLogin} variant="ghost" size="sm" className="gap-1">
                    <ArrowLeft className="h-3 w-3" />
                    {i18n.t('common.returnToLogin', 'Return to Login')}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
            {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
              <details className="mt-4 p-3 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-auto max-h-64">
                <summary className="cursor-pointer font-medium mb-2">Debug Info</summary>
                <pre className="whitespace-pre-wrap break-words">
                  {this.state.error?.stack}
                  {'\n\nComponent Stack:'}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * A lightweight wrapper for section-level error boundaries
 * Use this to wrap individual features/sections of the app
 */
export function SectionErrorBoundary({
  children,
  name,
  onReset
}: {
  children: ReactNode;
  name: string;
  onReset?: () => void;
}) {
  return (
    <ErrorBoundary inline sectionName={name} onReset={onReset}>
      {children}
    </ErrorBoundary>
  );
}
